import { useEffect, useMemo, useState } from "react";
import { TrendingUp, Sparkles, ArrowUpRight, ArrowDownRight, Check, Loader2, Activity, DollarSign } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function TCO() {
  const [months, setMonths] = useState(12);
  const [tco, setTco] = useState(null);
  const [yieldSugg, setYieldSugg] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tco");

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/analytics/tco?months=${months}`),
        api.get(`/yield/suggestions?lookahead_days=7`),
      ]);
      setTco(a.data); setYieldSugg(b.data || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [months]);

  const applyYield = async (car_id) => {
    try {
      const r = await api.post(`/yield/apply/${car_id}`);
      toast.success(`Prezzo aggiornato a €${r.data.new_price}`);
      load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const topPos = useMemo(() => (tco?.rows || []).filter((r) => r.net_margin > 0).slice(0, 10), [tco]);
  const topNeg = useMemo(() => (tco?.rows || []).filter((r) => r.net_margin < 0).slice(-10).reverse(), [tco]);

  return (
    <div className="space-y-6" data-testid="tco-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <TrendingUp className="text-[#ff0033]" size={26} />
            Business Intelligence
          </h1>
          <p className="text-sm text-white/45 mt-1">TCO per veicolo · ROI · utilizzo · Yield Engine pricing predittivo</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-white/40">Periodo</span>
          <select value={months} onChange={(e) => setMonths(+e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-1.5 text-sm">
            <option value={3}>3 mesi</option>
            <option value={6}>6 mesi</option>
            <option value={12}>12 mesi</option>
            <option value={24}>24 mesi</option>
          </select>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-white/10">
        {[
          { k: "tco", l: "TCO & ROI", i: DollarSign },
          { k: "yield", l: "Yield Engine", i: Sparkles },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`tco-tab-${t.k}`}
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 transition ${
              tab === t.k ? "text-white border-[#ff0033]" : "text-white/45 border-transparent hover:text-white"
            }`}>
            <t.i size={14} /> {t.l}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-12 text-white/40">Caricamento…</div>}

      {!loading && tab === "tco" && tco && (
        <>
          {/* KPI FLOTTA */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Veicoli flotta" value={tco.summary.cars_total} accent="white" />
            <KPI label="Revenue totale" value={`€ ${(tco.summary.fleet_revenue || 0).toLocaleString("it-IT")}`} accent="emerald" />
            <KPI label="Costi totali" value={`€ ${(tco.summary.fleet_cost || 0).toLocaleString("it-IT")}`} accent="rose" />
            <KPI label="Margine netto" value={`€ ${(tco.summary.fleet_margin || 0).toLocaleString("it-IT")}`} accent={tco.summary.fleet_margin >= 0 ? "emerald" : "rose"} />
            <KPI label="Utilizzo medio" value={`${tco.summary.fleet_utilization}%`} accent="amber" />
          </div>

          {/* BEST / WORST */}
          {tco.summary.best_performer && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PerfCard title="Top Performer" car={tco.summary.best_performer} tone="emerald" />
              <PerfCard title="Da Valutare" car={tco.summary.worst_performer} tone="rose" />
            </div>
          )}

          {/* TABLE TCO */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-xs" data-testid="tco-table">
                <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="px-3 py-2 text-left">Veicolo</th>
                    <th className="px-2 py-2 text-right">Contratti</th>
                    <th className="px-2 py-2 text-right">Giorni Noleggio</th>
                    <th className="px-2 py-2 text-right">Revenue</th>
                    <th className="px-2 py-2 text-right">Costi</th>
                    <th className="px-2 py-2 text-right">Margine €</th>
                    <th className="px-2 py-2 text-right">ROI %</th>
                    <th className="px-2 py-2 text-right">Utilizzo</th>
                  </tr>
                </thead>
                <tbody>
                  {tco.rows.map((r) => (
                    <tr key={r.car_id} className="border-t border-white/5">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{r.marca} {r.modello}</div>
                        <div className="text-[10px] font-mono text-white/40">{r.targa}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">{r.contracts_count}</td>
                      <td className="px-2 py-2 text-right font-mono">{r.rental_days}</td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-300">€{r.revenue.toLocaleString("it-IT")}</td>
                      <td className="px-2 py-2 text-right font-mono text-rose-300">€{r.total_cost.toLocaleString("it-IT")}</td>
                      <td className={`px-2 py-2 text-right font-mono font-bold ${r.net_margin >= 0 ? "text-emerald-300" : "text-rose-300"}`}>€{r.net_margin.toLocaleString("it-IT")}</td>
                      <td className={`px-2 py-2 text-right font-mono ${r.roi_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.roi_pct}%</td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-block w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-500" style={{ width: `${Math.min(100, r.utilization_pct)}%` }} />
                        </div>
                        <div className="text-[9px] text-white/40 mt-1">{r.utilization_pct}%</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && tab === "yield" && (
        <>
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="text-amber-300" size={20} />
              <div className="font-display font-bold text-lg">Yield Engine v1 — Pricing Predittivo</div>
            </div>
            <p className="text-xs text-white/55">
              Algoritmo basato su <b>saturazione segmento + saturazione veicolo + stagionalità IT</b>. Suggerisce un nuovo prezzo giornaliero ottimale per ogni veicolo nei prossimi 7 giorni.
              Clicca <span className="text-emerald-300 font-bold">✓ Applica</span> per aggiornare istantaneamente il prezzo veicolo (e ricalcolare settimanale/mensile coerenti).
            </p>
          </div>

          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-xs" data-testid="yield-table">
                <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="px-3 py-2 text-left">Veicolo</th>
                    <th className="px-2 py-2 text-right">€ Attuale</th>
                    <th className="px-2 py-2 text-right">€ Suggerito</th>
                    <th className="px-2 py-2 text-right">Δ %</th>
                    <th className="px-2 py-2 text-right">Occ. Veicolo</th>
                    <th className="px-2 py-2 text-right">Occ. Segmento</th>
                    <th className="px-2 py-2 text-center">Azione</th>
                    <th className="px-2 py-2 text-center">Applica</th>
                  </tr>
                </thead>
                <tbody>
                  {yieldSugg.map((y) => (
                    <tr key={y.car_id} className="border-t border-white/5">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{y.marca} {y.modello}</div>
                        <div className="text-[10px] font-mono text-white/40">{y.targa}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">€{y.prezzo_attuale}</td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-white">€{y.prezzo_suggerito}</td>
                      <td className={`px-2 py-2 text-right font-mono font-bold inline-flex items-center justify-end gap-1 w-full ${y.delta_pct > 0 ? "text-emerald-300" : y.delta_pct < 0 ? "text-rose-300" : "text-white/50"}`}>
                        {y.delta_pct > 0 ? <ArrowUpRight size={11} /> : y.delta_pct < 0 ? <ArrowDownRight size={11} /> : null}
                        {y.delta_pct}%
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-white/60">{y.occupancy_veicolo}%</td>
                      <td className="px-2 py-2 text-right font-mono text-white/60">{y.occupancy_segmento}%</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${
                          y.azione_suggerita === "INCREMENTA" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                          y.azione_suggerita === "RIDUCI" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" :
                          "bg-white/5 text-white/45 border-white/10"
                        }`}>{y.azione_suggerita}</span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => applyYield(y.car_id)} data-testid={`yield-apply-${y.targa}`}
                          disabled={y.delta_pct === 0}
                          className="px-3 py-1 rounded text-[10px] font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-30">
                          <Check size={11} className="inline" /> Applica
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const KPI = ({ label, value, accent }) => {
  const tones = { white: "text-white", emerald: "text-emerald-300", rose: "text-rose-300", amber: "text-amber-300" };
  return (
    <div className="glass rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-display font-black text-xl mt-1 ${tones[accent] || "text-white"}`}>{value}</div>
    </div>
  );
};

const PerfCard = ({ title, car, tone }) => {
  const tones = {
    emerald: "border-emerald-500/40 from-emerald-500/10",
    rose: "border-rose-500/40 from-rose-500/10",
  };
  if (!car) return null;
  return (
    <div className={`glass rounded-2xl p-4 border-l-4 bg-gradient-to-r to-transparent ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{title}</div>
      <div className="flex items-center gap-3">
        {car.foto && <img src={car.foto} alt="" className="w-16 h-16 object-cover rounded-lg" onError={(e) => e.target.style.display = "none"} />}
        <div>
          <div className="font-display font-bold text-lg">{car.marca} {car.modello}</div>
          <div className="text-[11px] font-mono text-white/50">{car.targa}</div>
          <div className={`text-2xl font-black mt-1 ${tone === "emerald" ? "text-emerald-300" : "text-rose-300"}`}>€{car.net_margin.toLocaleString("it-IT")}</div>
          <div className="text-[10px] text-white/50">{car.contracts_count} contratti · ROI {car.roi_pct}% · Utilizzo {car.utilization_pct}%</div>
        </div>
      </div>
    </div>
  );
};
